import { Component, inject, OnInit, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../../shared/services/user.service';
import { TenantService } from '../../../shared/services/tenant.service';
import { User, Tenant } from '../../../shared/models/user.model';
import { ConfirmModalComponent } from '../../../shared/utils/modal-validation/modal-validation';
import { EditEmployeeTenantsComponent } from '../edit-employee-tenants/edit-employee-tenants';
import { AddEmployeeComponent } from '../add-employee/add-employee';

@Component({
  selector: 'app-list-employee',
  imports: [CommonModule, FormsModule, ConfirmModalComponent, EditEmployeeTenantsComponent, AddEmployeeComponent],
  templateUrl: './list-employee.html'
})
export class ListEmployeeComponent implements OnInit {
  private userService = inject(UserService);
  private tenantService = inject(TenantService);

  loading = true;
  allEmployees: User[] = [];
  filteredEmployees: User[] = [];

  tenants: Tenant[] = [];
  selectedTenantId: string = '';
  searchQuery = '';

  // Add-employee modal
  showAddModal = false;

  closeAddModal() {
    this.showAddModal = false;
    this.userService.listUsers();
  }

  /** Archived employees are hidden unless the admin asks for them. */
  statusFilter: 'ACTIVE' | 'ARCHIVED' | 'ALL' = 'ACTIVE';

  // Menu and confirmation state
  openMenuId?: string;
  showConfirmDelete = false;
  employeeToDelete?: string;
  showConfirmArchive = false;
  employeeToArchive?: User;

  // Tenant edit modal state
  showTenantModal = false;
  employeeToEditTenants?: User;

  // Close menu when clicking outside
  @HostListener('document:click')
  onDocumentClick() {
    this.openMenuId = undefined;
  }

  toggleMenu(employeeId: string | undefined) {
    if (!employeeId) return;
    this.openMenuId = this.openMenuId === employeeId ? undefined : employeeId;
  }

  async ngOnInit() {
    this.tenants = await this.tenantService.listTenants();
    this.userService.users$.subscribe(users => {
      this.allEmployees = users;
      this.loading = false;
      this.filterEmployees();
    });
  }

  filterEmployees() {
    const q = this.searchQuery.trim().toLowerCase();
    let employees = this.allEmployees.filter(e => {
      const tenantOk = !this.selectedTenantId || e.tenants?.some(ut => ut.tenantId === this.selectedTenantId);
      const searchOk = !q ||
        `${e.firstName ?? ''} ${e.name ?? ''}`.toLowerCase().includes(q) ||
        (e.email ?? '').toLowerCase().includes(q);
      return tenantOk && searchOk;
    });

    if (this.statusFilter === 'ACTIVE') {
      employees = employees.filter(e => !e.isArchived);
    } else if (this.statusFilter === 'ARCHIVED') {
      employees = employees.filter(e => !!e.isArchived);
    }

    this.filteredEmployees = employees;
  }

  get archivedCount(): number {
    return this.allEmployees.filter(e => !!e.isArchived).length;
  }

  getEmployeeTenantNames(employee: User): string {
    if (!employee.tenants || employee.tenants.length === 0) return 'No tenant';
    return employee.tenants
      .map(ut => ut.tenant?.name || 'Unknown')
      .join(', ');
  }

  // Tenant modal methods
  openTenantModal(emp: User) {
    this.openMenuId = undefined;
    this.employeeToEditTenants = emp;
    this.showTenantModal = true;
  }

  closeTenantModal() {
    this.showTenantModal = false;
    this.employeeToEditTenants = undefined;
  }

  onTenantsSaved() {
    // Refresh will happen automatically via the subscription
    this.userService.listUsers();
  }

  confirmDelete(employeeId: string | undefined) {
    if (!employeeId) return;
    this.openMenuId = undefined;
    this.employeeToDelete = employeeId;
    this.showConfirmDelete = true;
  }

  async handleConfirmDelete(result: 'ok' | 'cancel') {
    this.showConfirmDelete = false;
    if (result === 'ok' && this.employeeToDelete) {
      await this.deleteUser(this.employeeToDelete);
    }
    this.employeeToDelete = undefined;
  }

  async deleteUser(userId: string) {
    try {
      await this.userService.deleteUser(userId);
      this.allEmployees = this.allEmployees.filter(e => e.id !== userId);
      this.filterEmployees();
    } catch (error) {
      console.error("Error deleting", error);
      alert("Impossible to delete the user.");
    }
  }

  // Archive / restore
  confirmArchive(emp: User) {
    this.openMenuId = undefined;
    this.employeeToArchive = emp;
    this.showConfirmArchive = true;
  }

  get archiveConfirmMessage(): string {
    const emp = this.employeeToArchive;
    if (!emp) return '';
    const fullName = `${emp.firstName || ''} ${emp.name || ''}`.trim();
    return emp.isArchived
      ? `Restore ${fullName}? Their account will be re-enabled and they will be able to sign in again.`
      : `Archive ${fullName}? Their account will be disabled and they will no longer be able to sign in, but all their data stays available to admins.`;
  }

  async handleConfirmArchive(result: 'ok' | 'cancel') {
    this.showConfirmArchive = false;
    const emp = this.employeeToArchive;
    this.employeeToArchive = undefined;
    if (result !== 'ok' || !emp?.id) return;

    try {
      if (emp.isArchived) await this.userService.restoreUser(emp.id);
      else await this.userService.archiveUser(emp.id);
    } catch (error) {
      console.error('Error archiving/restoring employee', error);
      alert(`Impossible to ${emp.isArchived ? 'restore' : 'archive'} the user.`);
    }
  }

  async updateRole(emp: User, event: any) {
    if (!emp.id) return;
    const oldRole = emp.role;
    const newRole = event.target.value;
    if (!confirm(`Are you sure you want to change role from ${oldRole} to ${newRole}?`)) {
      emp.role = oldRole;
      return;
    }
    try {
      emp.role = newRole;
      await this.userService.updateUserRole(emp.id, newRole);
      console.log('Role updated successfully');

    } catch (err) {
      console.error(err);
      alert("Impossible to update the role. Permission denied or error.");
      emp.role = oldRole;
    }
  }
}
